import {format} from 'date-fns';
import {isNumber} from 'lodash-es';
import type {
	Gsoa_ShoppingList_GetItemsCollection as GsoaShoppingListGetItemsCollection,
	Gsoa_ShoppingList_UpdateItem as GsoaShoppingListUpdateItem,
	Gsoa_ProductCatalog_ProductDetailForShoppingList as ProductDetailForShoppingList,
	Gsoa_ProductCatalog_ProductDetail as ProductDetail,
} from '~/openapi';
import {Ribbon} from '~/types/product';
import {
	getDisplayedPrice,
	getNormalPrice,
} from '~/utils/triplePricing';

export interface GsoaShoppingListGetShoppingListresponseWithProductDetail extends GsoaShoppingListGetItemsCollection {
	productDetail?: ProductDetail & ProductDetailForShoppingList;
	CreatedAt?: string;
}

export interface Category {
	name: string;
	items: GsoaShoppingListGetShoppingListresponseWithProductDetail[];
	isPurchasedCategory?: boolean;
}

export const PURCHASED_CATEGORY_NAME = 'Nakoupeno';

const OTHER_CATEGORY_NAME = 'Ostatní';

export function isPurchasedCategory(category: Pick<Category, 'isPurchasedCategory' | 'name'>) {
	return category.isPurchasedCategory === true || category.name === PURCHASED_CATEGORY_NAME;
}

function createPurchasedCategory(
	items: GsoaShoppingListGetShoppingListresponseWithProductDetail[],
): Category | null {
	if (items.length === 0) {
		return null;
	}
	return {
		name: PURCHASED_CATEGORY_NAME,
		items: sortItemsByVisualOrder(items),
		isPurchasedCategory: true,
	};
}

export type ShoppingListSortMode = 'category' | 'insertion';

export const SHOPPING_LIST_SORT_MODE_KEY_PREFIX = 'shopping-list-sort-mode:';

export function getShoppingListSortModeStorageKey(listId: string | number) {
	return `${SHOPPING_LIST_SORT_MODE_KEY_PREFIX}${listId}`;
}

function getCategoryName(item: GsoaShoppingListGetShoppingListresponseWithProductDetail) {
	return item.productDetail?.mainCategory?.categoryName ?? OTHER_CATEGORY_NAME;
}

function sortItemsByVisualOrder(items: GsoaShoppingListGetShoppingListresponseWithProductDetail[]) {
	return [...items]
		.map((item, index) => ({item, index}))
		.sort((a, b) => {
			const aOrder = isNumber(a.item.VisualOrder) ? a.item.VisualOrder : Number.MAX_SAFE_INTEGER;
			const bOrder = isNumber(b.item.VisualOrder) ? b.item.VisualOrder : Number.MAX_SAFE_INTEGER;
			if (aOrder !== bOrder) {
				return aOrder - bOrder;
			}
			return a.index - b.index;
		})
		.map(({item}) => item);
}

function defaultCategoryOrder(categoryNames: string[]) {
	const others = categoryNames
		.filter((name) => name !== OTHER_CATEGORY_NAME)
		.sort((a, b) => a.localeCompare(b));
	return categoryNames.includes(OTHER_CATEGORY_NAME)
		? [...others, OTHER_CATEGORY_NAME]
		: others;
}

function getItemLinePayPrice(product: GsoaShoppingListGetShoppingListresponseWithProductDetail) {
	const calculatedPrice = product.productDetail?.calculatedPrice?.currentPrice;
	if (isNumber(calculatedPrice)) {
		return calculatedPrice;
	}

	const productInHouse = product.productDetail?.productInHouse;
	return productInHouse ? getDisplayedPrice(productInHouse) : 0;
}

function getItemLineNormalPrice(product: GsoaShoppingListGetShoppingListresponseWithProductDetail) {
	const calculatedNormalPrice = product.productDetail?.calculatedPrice?.normalPrice;
	if (isNumber(calculatedNormalPrice)) {
		return calculatedNormalPrice;
	}

	const productInHouse = product.productDetail?.productInHouse;
	if (productInHouse) {
		const normalPrice = getNormalPrice(productInHouse);
		if (normalPrice > 0) {
			return normalPrice;
		}
	}

	return 0;
}

function sumShoppingListItemPrices(
	products: GsoaShoppingListGetShoppingListresponseWithProductDetail[],
	getUnitPrice: (product: GsoaShoppingListGetShoppingListresponseWithProductDetail) => number,
) {
	return products.reduce((total, product) => {
		const amount = product.Amount ?? 1;
		return total + amount * getUnitPrice(product);
	}, 0);
}

function buildCategoryBlocksFromItems(items: GsoaShoppingListGetShoppingListresponseWithProductDetail[]) {
	const orderedItems = sortItemsByVisualOrder(items);

	const blocks: Category[] = [];
	for (const item of orderedItems) {
		const categoryName = getCategoryName(item);
		const lastBlock = blocks[blocks.length - 1];
		if (lastBlock?.name === categoryName) {
			lastBlock.items.push(item);
		} else {
			blocks.push({name: categoryName, items: [item]});
		}
	}

	const blockNames = blocks.map((block) => block.name);
	const hasContiguousCategoryBlocks = new Set(blockNames).size === blockNames.length;

	if (hasContiguousCategoryBlocks && blocks.length > 0) {
		return blocks;
	}

	const categorizedList: Record<string, Category> = {};
	for (const item of orderedItems) {
		const categoryName = getCategoryName(item);
		if (!categorizedList[categoryName]) {
			categorizedList[categoryName] = {name: categoryName, items: []};
		}
		categorizedList[categoryName].items.push(item);
	}

	return defaultCategoryOrder(Object.keys(categorizedList)).map((name) => categorizedList[name]);
}

export function sortedShoppingListByCategory(items: GsoaShoppingListGetShoppingListresponseWithProductDetail[]) {
	const {toBuy, purchased} = partitionItemsByPurchased(items);
	const activeCategories = buildCategoryBlocksFromItems(toBuy);
	const purchasedCategory = createPurchasedCategory(purchased);

	return purchasedCategory
		? [...activeCategories, purchasedCategory]
		: activeCategories;
}

export function sortedShoppingListByInsertion(items: GsoaShoppingListGetShoppingListresponseWithProductDetail[]) {
	return [...items]
		.map((item, index) => ({item, index}))
		.sort((a, b) => {
			const aCreated = a.item.CreatedAt ?? a.item.LastUpdate ?? '';
			const bCreated = b.item.CreatedAt ?? b.item.LastUpdate ?? '';
			if (aCreated !== bCreated) {
				return aCreated.localeCompare(bCreated);
			}
			const aOrder = isNumber(a.item.VisualOrder) ? a.item.VisualOrder : Number.MAX_SAFE_INTEGER;
			const bOrder = isNumber(b.item.VisualOrder) ? b.item.VisualOrder : Number.MAX_SAFE_INTEGER;
			if (aOrder !== bOrder) {
				return aOrder - bOrder;
			}
			if (a.item.ID !== b.item.ID) {
				return a.item.ID - b.item.ID;
			}
			return a.index - b.index;
		})
		.map(({item}) => item);
}

export function partitionItemsByPurchased(items: GsoaShoppingListGetShoppingListresponseWithProductDetail[]) {
	const toBuy: GsoaShoppingListGetShoppingListresponseWithProductDetail[] = [];
	const purchased: GsoaShoppingListGetShoppingListresponseWithProductDetail[] = [];

	for (const item of items) {
		if (item.Purchased) {
			purchased.push(item);
		} else {
			toBuy.push(item);
		}
	}

	return {toBuy, purchased};
}

export function getActiveShoppingListItemsCount(
	items: Array<Pick<GsoaShoppingListGetItemsCollection, 'Purchased'>>,
) {
	return items.filter((item) => !item.Purchased).length;
}

export const getTotalPrice = (products: GsoaShoppingListGetShoppingListresponseWithProductDetail[]) => (
	sumShoppingListItemPrices(products, getItemLinePayPrice)
);

export const getOriginalPriceSum = (products: GsoaShoppingListGetShoppingListresponseWithProductDetail[]) => (
	sumShoppingListItemPrices(products, getItemLineNormalPrice)
);

function getItemSavings(product: GsoaShoppingListGetShoppingListresponseWithProductDetail) {
	if (product.productDetail?.productInHouse?.originalPrice == null) {
		return 0;
	}

	const normalPrice = getItemLineNormalPrice(product);
	const payPrice = getItemLinePayPrice(product);
	const savings = normalPrice - payPrice;

	if (savings <= 0) {
		return 0;
	}

	return (product.Amount ?? 1) * savings;
}

export function getSavedAmount(products: GsoaShoppingListGetShoppingListresponseWithProductDetail[]) {
	return products.reduce((total, product) => total + getItemSavings(product), 0);
}

export function computeShoppingListSummary(
	items: GsoaShoppingListGetShoppingListresponseWithProductDetail[],
) {
	const totalPrice = getTotalPrice(items);
	const originalPriceSum = getOriginalPriceSum(items);
	const savedAmount = getSavedAmount(items);
	const {toBuy, purchased} = partitionItemsByPurchased(items);

	return {
		totalPrice,
		originalPriceSum,
		savedAmount,
		toBuyPrice: getTotalPrice(toBuy),
		purchasedPrice: getTotalPrice(purchased),
		textNotesCount: items.filter(isTextShoppingListItem).length,
	};
}

export function isTextShoppingListItem(item: Pick<GsoaShoppingListGetItemsCollection, 'VANR'>) {
	return !item.VANR;
}

export function matchesShoppingListItemData(
	item: Pick<GsoaShoppingListGetItemsCollection, 'VANR' | 'Text'>,
	itemData: Pick<GsoaShoppingListUpdateItem, 'VANR' | 'Text'>,
) {
	if (itemData.VANR) {
		return item.VANR === itemData.VANR;
	}
	return isTextShoppingListItem(item)
		&& item.Text.trim().toLowerCase() === itemData.Text.trim().toLowerCase();
}

export function getBonusPointsFromProduct(product: ProductDetailForShoppingList): number {
	return product.ribbon?.[Ribbon.CASHBACK]?.bonusCredit ?? 0;
}

export function appendShoppingListDateSuffix(name: string, date = new Date()): string {
	return `${name} (${format(date, 'dd.MM.yyyy')})`;
}

export function formatShoppingListEmailDate(date: Date): string {
	return format(date, 'd. M. yyyy');
}

const EXCLUDES_TEXT_NOTES_FOOTNOTE_FORMS = [
	'Nezahrnuje {n} položku, která je jako poznámka',
	'Nezahrnuje {n} položky, které jsou jako poznámka',
	'Nezahrnuje {n} položek, které jsou jako poznámka',
] as const;

function getCzechPluralFormIndex(count: number): 0 | 1 | 2 {
	if (count === 1) {
		return 0;
	}

	const mod10 = count % 10;
	const mod100 = count % 100;

	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
		return 1;
	}

	return 2;
}

export function formatShoppingListExcludesTextNotesFootnote(count: number): string | null {
	if (count <= 0) {
		return null;
	}

	const template = EXCLUDES_TEXT_NOTES_FOOTNOTE_FORMS[getCzechPluralFormIndex(count)];
	return template.replace('{n}', String(count));
}
